import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { NAV, NAV_MORE } from "../data/content";
import { ServerStatus } from "./ServerStatus";
import { useSiteConfig } from "../context/SiteConfig";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "./ui/dropdown-menu";
import { ChevronDown } from "lucide-react";

const useLenis = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
};

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { logo, link } = useSiteConfig();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "backdrop-blur-xl bg-obsidian/70 border-b border-white/5" : "bg-transparent"}`}>
      <div className="mx-auto max-w-7xl px-6 sm:px-10 flex items-center justify-between h-20">
        <Link to="/" data-testid="nav-logo" className="group flex items-center gap-3 shrink-0 mr-6">
          <img src={logo} alt="Aurora Springs" className="h-11 w-11 rounded-full ring-1 ring-gold/40 group-hover:ring-teal transition-all" />
          <span className="hidden sm:block font-display text-2xl tracking-tight leading-none">Aurora<span className="text-teal">Springs</span></span>
        </Link>
        <nav className="hidden lg:flex items-center gap-x-4 xl:gap-x-6">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => `font-feature text-[11px] xl:text-xs uppercase tracking-[0.15em] whitespace-nowrap transition-colors ${isActive ? "text-teal" : "text-[#8F877C] hover:text-bone"}`}>
              {n.label}
            </NavLink>
          ))}
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="nav-more" className="flex items-center gap-1 font-feature text-[11px] xl:text-xs uppercase tracking-[0.15em] text-[#8F877C] hover:text-bone transition-colors outline-none">
              More <ChevronDown className="h-3 w-3" />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-obsidian/95 backdrop-blur-xl border-[#2A201A] rounded-none">
              {NAV_MORE.map((n) => (
                <DropdownMenuItem key={n.to} asChild>
                  <Link to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="font-feature text-xs uppercase tracking-[0.15em] text-[#8F877C] focus:text-teal cursor-pointer">
                    {n.label}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>
        <div className="hidden lg:flex items-center gap-4 shrink-0 ml-6">
          <div className="hidden xl:block"><ServerStatus /></div>
          <a href={link("connect")} target="_blank" rel="noreferrer" data-testid="nav-connect-btn"
            className="font-feature text-xs uppercase tracking-[0.2em] bg-teal text-obsidian px-5 py-3 hover:bg-bone transition-colors">Connect</a>
        </div>
        <button className="lg:hidden text-bone" onClick={() => setOpen(true)} data-testid="mobile-menu-open" aria-label="Open menu"><Menu /></button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="lg:hidden fixed inset-0 z-50 bg-obsidian/98 backdrop-blur-xl p-8">
            <div className="flex justify-between items-center mb-12">
              <img src={logo} alt="Aurora Springs" className="h-12 w-12 rounded-full ring-1 ring-gold/40" />
              <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu"><X className="text-bone" /></button>
            </div>
            <div className="flex flex-col gap-6">
              {[...NAV, ...NAV_MORE].map((n) => (
                <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)}
                  className={({ isActive }) => `font-display text-3xl ${isActive ? "text-teal" : "text-bone"}`}>{n.label}</NavLink>
              ))}
              <a href={link("connect")} target="_blank" rel="noreferrer" className="mt-6 font-feature text-sm uppercase tracking-widest bg-teal text-obsidian px-6 py-4 text-center">Connect Now</a>
              <div className="mt-4"><ServerStatus /></div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

const Footer = () => {
  const { logo, link, footerTagline } = useSiteConfig();
  return (
  <footer className="border-t border-[#2A201A] bg-surface/30 mt-24">
    <div className="mx-auto max-w-7xl px-6 sm:px-10 py-16 grid gap-12 md:grid-cols-4">
      <div className="md:col-span-2">
        <div className="flex items-center gap-3">
          <img src={logo} alt="Aurora Springs" className="h-14 w-14 rounded-full ring-1 ring-gold/40" />
          <span className="font-display text-3xl">Aurora<span className="text-teal">Springs</span></span>
        </div>
        <p className="mt-5 max-w-md text-[#8F877C] leading-relaxed">{footerTagline}</p>
        <div className="mt-6"><ServerStatus /></div>
      </div>
      <div>
        <p className="font-feature text-xs uppercase tracking-[0.25em] text-teal mb-5">Explore</p>
        <ul className="space-y-3">
          {NAV.slice(0, 5).map((n) => (
            <li key={n.to}><Link to={n.to} className="text-[#8F877C] hover:text-bone transition-colors">{n.label}</Link></li>
          ))}
        </ul>
      </div>
      <div>
        <p className="font-feature text-xs uppercase tracking-[0.25em] text-teal mb-5">Join</p>
        <ul className="space-y-3">
          <li><a href={link("connect")} target="_blank" rel="noreferrer" className="text-[#8F877C] hover:text-bone transition-colors">Direct Connect</a></li>
          <li><a href={link("discord")} target="_blank" rel="noreferrer" className="text-[#8F877C] hover:text-bone transition-colors">Discord</a></li>
          <li className="font-mono text-xs text-[#5c554c] pt-2">cfx.re/join/{link("joinCode")}</li>
        </ul>
      </div>
    </div>
    <div className="border-t border-[#2A201A] py-6 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 text-center font-mono text-xs text-[#5c554c]">
      <span>© {new Date().getFullYear()} Aurora Springs Roleplay — Not affiliated with Rockstar Games.</span>
      <Link to="/admin" className="hover:text-teal transition-colors uppercase tracking-widest" data-testid="footer-admin-link">Admin</Link>
      <Link to="/staff-panel" className="hover:text-teal transition-colors uppercase tracking-widest" data-testid="footer-staff-link">Staff</Link>
    </div>
  </footer>
  );
};

export const Layout = ({ children }) => {
  useLenis();
  return (
    <div className="grain relative min-h-screen">
      <Nav />
      <main>{children}</main>
      <Footer />
    </div>
  );
};
